import React from 'react'
import StarIcons from '@/icons/StarIcons'
import KebabVerticalIcons from '@/icons/KebabVerticalIcons'
import { Checkbox } from '@/components/ui/checkbox'

interface FileBlockProps {
    filename: string
    isStarred: boolean
    thumbnail?: string | null
    icon: React.JSX.Element
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void
    isSelected?: boolean
    isSelectedMode?: boolean
}

const FileBlock = React.forwardRef<HTMLDivElement, FileBlockProps>(
    (
        {
            filename,
            isStarred,
            thumbnail,
            icon,
            onClick,
            isSelected = false,
            isSelectedMode = false,
        },
        ref
    ) => (
        <div
            ref={ref}
            className={`w-full flex flex-col px-2 py-2 shadow-sm cursor-pointer gap-2 rounded-lg relative ${
                isSelected && isSelectedMode
                    ? 'bg-blue-100 border-2 border-blue-400 hover:bg-blue-200'
                    : 'bg-[#F9FBFF] hover:bg-gray-200'
            }`}
            onClick={onClick}
            onContextMenu={onClick}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    {isSelectedMode && (
                        <div className="size-5 shrink-0">
                            <Checkbox
                                className="data-[state=checked]:border-white data-[state=checked]:text-white"
                                checked={isSelected}
                            />
                        </div>
                    )}
                    <div className="size-5 shrink-0">{icon}</div>
                    <p className="text-sm text-gray-700 truncate">{filename}</p>
                </div>
                <div className="flex items-center">
                    {isStarred && (
                        <div className="size-4 shrink-0">
                            <StarIcons className="text-[#FFD600]" />
                        </div>
                    )}
                    <div
                        className="pl-2"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            // Prefer dispatching on the FileBlock root via forwarded ref
                            const rootEl = (
                                ref && 'current' in (ref as any)
                                    ? (
                                          ref as React.MutableRefObject<HTMLDivElement | null>
                                      ).current
                                    : null
                            ) as HTMLDivElement | null

                            const targetEl =
                                rootEl ||
                                ((e.currentTarget as HTMLElement).closest(
                                    '[data-file-row]'
                                ) as HTMLElement | null) ||
                                (e.currentTarget as HTMLElement)

                            targetEl.dispatchEvent(
                                new MouseEvent('contextmenu', {
                                    bubbles: true,
                                    clientX: (e as React.MouseEvent).clientX,
                                    clientY: (e as React.MouseEvent).clientY,
                                })
                            )
                        }}
                    >
                        <KebabVerticalIcons className="text-gray-500 size-5 hover:text-gray-700 hover:scale-110 cursor-pointer" />
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-center overflow-hidden rounded-lg aspect-video">
                {thumbnail ? (
                    <img
                        src={thumbnail}
                        alt="File thumbnail"
                        className="object-cover w-full h-full"
                    />
                ) : (
                    <div className="size-24">{icon}</div>
                )}
            </div>
        </div>
    )
)

export default FileBlock
